import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ptBR from '../../locales/pt-BR/translation.json'
import enUS from '../../locales/en-US/translation.json'
import es419 from '../../locales/es-419/translation.json'
import { resolveInitialLocale, USER_LOCALE_KEY } from '../locale-detection'

/**
 * Hardcoded bootstrap list — must match Harness V1 spec
 * (`docs/superpowers/specs/2026-05-12-axiomgo-harness-v1-design.md` § 1).
 *
 * The i18n bundle MUST load synchronously on first render to avoid FOUC.
 * The engine GET /api/v1/i18n/config call later may narrow/widen this list
 * at runtime, but bootstrap NEVER depends on the engine being reachable.
 */
const BOOTSTRAP_SUPPORTED = ['pt-BR', 'en-US', 'es-419'] as const
const BOOTSTRAP_DEFAULT_LANG = 'pt-BR'
const BOOTSTRAP_FALLBACK_LANG = 'en-US'

const initialLocale = resolveInitialLocale(localStorage, navigator, [...BOOTSTRAP_SUPPORTED])

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': { translation: ptBR },
      'en-US': { translation: enUS },
      'es-419': { translation: es419 },
    },
    lng: initialLocale,
    fallbackLng: BOOTSTRAP_FALLBACK_LANG,
    supportedLngs: [...BOOTSTRAP_SUPPORTED],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // `resolveInitialLocale` is the only locale decision point. The detector
      // may read an explicit choice, but must not replace it with navigator data.
      order: ['localStorage'],
      lookupLocalStorage: USER_LOCALE_KEY,
      caches: [],
    },
  })

export interface HarnessI18nConfig {
  supportedLanguages: string[]
  defaultLanguage: string
  fallbackLanguage: string
  detectionStrategy: 'first-turn-auto' | 'default-language'
  allowUserOverride: boolean
}

/**
 * Fetch the harness i18n config from the engine and reconcile against the
 * already-initialized i18next instance. Called from the app bootstrap AFTER
 * React mounts — never blocks first paint.
 *
 * Reconciliation rules (defensive — engine may be unreachable / dev mode):
 * - Fetch failure → keep BOOTSTRAP_* values. NEVER throw to caller.
 * - supportedLanguages narrows the bootstrap set → no-op (locales already
 *   bundled; engine list is advisory for detection only).
 * - no user override stored → switch to defaultLanguage (see
 *   `applyHarnessI18nConfig` for why `detectionStrategy` no longer gates this).
 * - allowUserOverride === false AND user override exists → reset to
 *   defaultLanguage and remove the stored override.
 *
 * STO-1401 — `authToken` autentica o fetch pelo MESMO canal de identidade que
 * o resto do app usa para falar com o motor (`useAuthStore.getState().getToken()`,
 * ver `chat-stream.ts`). `GET /api/v1/i18n/config` está atrás de `requireAuth`
 * no motor (`api/v1/index.ts`, hook global) — sem o header a chamada sempre
 * leva 401 e `defaultLanguage` nunca reflete a localização do TENANT (que o
 * motor só resolve com `request.tenantId`, populado pelo auth). `authToken`
 * ausente/null é um caso normal (boot antes do Clerk resolver a sessão, ou
 * usuário deslogado): a chamada segue sem `Authorization`, o motor devolve a
 * config GLOBAL, e o `!response.ok` abaixo já trata a falha sem lançar.
 *
 * Idempotent — safe to call multiple times.
 */
export async function reconcileHarnessI18nConfig(
  engineBaseUrl: string,
  fetchImpl: typeof fetch = fetch,
  storage: Pick<Storage, 'getItem' | 'removeItem'> = localStorage,
  authToken?: string | null,
): Promise<HarnessI18nConfig | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  let response: Response
  try {
    response = await fetchImpl(`${engineBaseUrl}/api/v1/i18n/config`, {
      method: 'GET',
      headers,
    })
  } catch {
    return null
  }

  if (!response.ok) return null

  let body: unknown
  try {
    body = await response.json()
  } catch {
    return null
  }

  const parsed = parseHarnessI18nConfig(body)
  if (!parsed) return null

  applyHarnessI18nConfig(parsed, storage)
  return parsed
}

function parseHarnessI18nConfig(value: unknown): HarnessI18nConfig | null {
  if (!value || typeof value !== 'object') return null
  const obj = value as Record<string, unknown>
  const supported = obj['supportedLanguages']
  if (
    !Array.isArray(supported) ||
    supported.length === 0 ||
    !supported.every((s): s is string => typeof s === 'string')
  ) {
    return null
  }
  const defaultLang = obj['defaultLanguage']
  const fallbackLang = obj['fallbackLanguage']
  const strategy = obj['detectionStrategy']
  const allowOverride = obj['allowUserOverride']
  if (typeof defaultLang !== 'string') return null
  if (typeof fallbackLang !== 'string') return null
  if (strategy !== 'first-turn-auto' && strategy !== 'default-language') return null
  if (typeof allowOverride !== 'boolean') return null
  return {
    supportedLanguages: supported,
    defaultLanguage: defaultLang,
    fallbackLanguage: fallbackLang,
    detectionStrategy: strategy,
    allowUserOverride: allowOverride,
  }
}

/**
 * STO-1401, ADENDO DO CTO (23/09/2026) — A REGRA DO `detectionStrategy`.
 *
 * `defaultLanguage` só passa a refletir a localização do TENANT depois da
 * STO-1399; `detectionStrategy` é uma config GLOBAL separada
 * (`harness.i18n.detectionStrategy`, motor) que o motor NÃO liga sozinho a
 * `defaultLanguage` — hoje ela nasce `'first-turn-auto'` por default de
 * fábrica e só muda para `'default-language'` por escrita administrativa
 * explícita (`PUT /config/:key`). Gatear a etapa 2 só em
 * `'default-language'` (como antes) deixaria a preferência do tenant morta
 * para TODO tenant que nunca recebeu essa escrita — exatamente o defeito que
 * a STO-1334 pediu para fechar.
 *
 * DECISÃO (frontend, sem mexer no motor): aplica `defaultLanguage` do tenant
 * nos DOIS valores de `detectionStrategy`, contanto que não haja escolha
 * local e o idioma não tenha mudado desde o boot (guarda de corrida abaixo).
 * `detectionStrategy` continua no contrato para o motor poder, no futuro,
 * anunciar um terceiro modo que precise de tratamento distinto — hoje ele não
 * muda o comportamento do chat.
 */
function applyHarnessI18nConfig(
  config: HarnessI18nConfig,
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
): void {
  applyHarnessI18nLists(config)
  // BUG (STO-1334): isto lia 'axg-locale'/'axiomgo-locale' — chaves que o
  // i18next-browser-languagedetector REGRAVA sozinho a cada changeLanguage
  // (inclusive o do boot). Depois do primeiro paint elas NUNCA ficam null, e
  // a etapa 2 (preferência do tenant) morria sempre. A escolha EXPLÍCITA do
  // usuário só existe em USER_LOCALE_KEY (RAIZ 10, `locale-detection.ts`).
  const userOverride = storage.getItem(USER_LOCALE_KEY)

  if (!config.allowUserOverride && userOverride !== null) {
    storage.removeItem(USER_LOCALE_KEY)
    storage.removeItem('axg-locale')
    storage.removeItem('axiomgo-locale')
    void i18n.changeLanguage(config.defaultLanguage)
    return
  }

  // Corrida com a preferência do USUÁRIO (persistida no motor, aplicada por
  // `aplicarIdiomaDoUsuario` logo que `/auth/status` responde): só aplica o
  // default do tenant se nada mudou o idioma desde o boot. Se `/auth/status`
  // já resolveu (ou o usuário trocou na mão) antes deste fetch voltar,
  // `i18n.language` já não é mais `initialLocale` e este bloco não pisa nele.
  if (userOverride === null && i18n.language === initialLocale) {
    void i18n.changeLanguage(config.defaultLanguage)
  }
}

/**
 * Idiomas aceitos e idioma reserva do painel Config do Admin. Antes só
 * `defaultLanguage` chegava no i18next; as duas listas ficavam nos valores do
 * boot e a mudança no Admin não tinha efeito. Só entram idiomas que têm pacote
 * de textos carregado (`hasResourceBundle`) — o motor pode anunciar um idioma
 * que a tela ainda não tem, e aí vale o que já estava valendo.
 */
export function applyHarnessI18nLists(
  config: Pick<HarnessI18nConfig, 'supportedLanguages' | 'fallbackLanguage'>,
): void {
  const bundled = (lng: string): boolean => i18n.hasResourceBundle(lng, 'translation')
  const supported = config.supportedLanguages.filter(bundled)
  if (supported.length > 0) {
    i18n.options.supportedLngs = [...supported, 'cimode']
  }
  if (bundled(config.fallbackLanguage)) {
    i18n.options.fallbackLng = [config.fallbackLanguage]
  }
}

export const __testing__ = {
  BOOTSTRAP_SUPPORTED,
  BOOTSTRAP_DEFAULT_LANG,
  BOOTSTRAP_FALLBACK_LANG,
  parseHarnessI18nConfig,
  applyHarnessI18nConfig,
}

export default i18n
